import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, filterParams, filterKey } from "@/lib/api";
import { fmtInt, fmtCost, fmtTime, timeAgo } from "@/lib/format";
import { ArrowUpRight } from "lucide-react";

export default function SessionsTable({ filters, setFilters }) {
  const { data = [] } = useQuery({
    queryKey: ["sessions", ...filterKey(filters)],
    queryFn: async () => (await api.get("/sessions", { params: filterParams(filters) })).data,
    refetchInterval: 20000,
  });

  return (
    <div className="bg-card border border-border" data-testid="sessions-table">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <p className="overline">sessions</p>
          <h3 className="text-sm font-medium mt-1">Per-session token &amp; cost totals</h3>
        </div>
        <span className="font-mono text-xs text-muted-foreground">{data.length} sessions</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full font-mono text-xs">
          <thead>
            <tr className="text-muted-foreground border-b border-border">
              {["Session", "User", "Started", "Last seen", "Input", "Output", "Cache", "Cost", "Models", "Token", "Events", ""].map((h) => (
                <th key={h} className="text-left font-normal px-3 py-2 whitespace-nowrap overline">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr><td colSpan={12} className="px-3 py-8 text-center text-muted-foreground">no sessions yet</td></tr>
            )}
            {data.map((s) => (
              <tr key={s.session_id} className="border-b border-border/50 hover:bg-white/[0.03] transition-colors" data-testid={`session-row-${s.session_id}`}>
                <td className="px-3 py-2.5 text-white whitespace-nowrap max-w-[220px] truncate">{s.session_id}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{s.user_id || "—"}</td>
                <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{fmtTime(s.started_at)}</td>
                <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{timeAgo(s.last_seen_at)}</td>
                <td className="px-3 py-2.5 text-blue-400 tabular-nums">{fmtInt(s.total_input_tokens)}</td>
                <td className="px-3 py-2.5 text-amber-400 tabular-nums">{fmtInt(s.total_output_tokens)}</td>
                <td className="px-3 py-2.5 text-zinc-400 tabular-nums">{fmtInt(s.total_cache_tokens)}</td>
                <td className="px-3 py-2.5 text-white tabular-nums">{fmtCost(s.total_cost)}</td>
                <td className="px-3 py-2.5 text-muted-foreground max-w-[160px] truncate">{(s.models_used || []).join(", ") || "—"}</td>
                <td className="px-3 py-2.5 text-muted-foreground max-w-[140px] truncate" data-testid={`session-token-${s.session_id}`}>{(s.source_tokens || []).join(", ") || "—"}</td>
                <td className="px-3 py-2.5 text-muted-foreground tabular-nums">{fmtInt(s.event_count)}</td>
                <td className="px-3 py-2.5">
                  <button
                    onClick={() => setFilters((f) => ({ ...f, session: s.session_id }))}
                    data-testid={`view-session-${s.session_id}`}
                    className="text-muted-foreground hover:text-white transition-colors"
                    title="Filter by this session"
                  >
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
